/**
 * Менеджер массовых операций с оптимизацией производительности
 */

import { EventEmitter } from 'eventemitter3';
import { generateId } from '../utils/helpers';
import {
  IBatchManager,
  BatchOperation,
  BatchConfig,
  BatchResult,
  BatchProgress,
  BatchEventListener,
  BatchManagerStatistics,
  BatchExecutionStrategy,
  BatchErrorMode,
  BatchOperationResult,
  BatchExecutionContext,
  BatchStatistics
} from '../types/batch-interfaces';

/**
 * Внутреннее состояние батча
 */
interface BatchState {
  id: string;
  config: BatchConfig;
  operations: BatchOperation[];
  progress: BatchProgress;
  result?: BatchResult;
  abortController?: AbortController;
  startTime?: Date;
  endTime?: Date;
  isExecuting: boolean;
  operationResults: Map<string, BatchOperationResult>;
  sharedData: Map<string, any>;
}

/**
 * Реализация менеджера массовых операций
 */
export class BatchManager extends EventEmitter implements IBatchManager {
  private batches = new Map<string, BatchState>();
  private eventListeners: BatchEventListener[] = [];
  private statistics: BatchManagerStatistics = {
    totalBatches: 0,
    successfulBatches: 0,
    failedBatches: 0,
    activeBatches: 0,
    totalOperations: 0,
    averageBatchExecutionTime: 0,
    memoryUsage: 0
  };

  private readonly defaultConfig: BatchConfig = {
    executionStrategy: BatchExecutionStrategy.PARALLEL,
    errorMode: BatchErrorMode.COLLECT_ERRORS,
    maxConcurrency: 10,
    timeout: 300000, // 5 минут
    progressUpdateInterval: 1000, // 1 секунда
    enableRollback: true,
    validateBeforeExecution: true,
    persistIntermediateResults: false
  };

  /**
   * Создать новый батч
   */
  createBatch(config?: Partial<BatchConfig>): string {
    const batchId = generateId();
    const finalConfig = { ...this.defaultConfig, ...config };
    
    const batchState: BatchState = {
      id: batchId,
      config: finalConfig,
      operations: [],
      progress: this.createInitialProgress(batchId),
      isExecuting: false,
      operationResults: new Map(),
      sharedData: new Map()
    };

    this.batches.set(batchId, batchState);
    this.statistics.activeBatches++;
    
    return batchId;
  }

  /**
   * Добавить операцию в батч
   */
  addOperation(batchId: string, operation: BatchOperation): void {
    const batch = this.batches.get(batchId);
    if (!batch) {
      throw new Error(`Батч с ID ${batchId} не найден`);
    }

    if (batch.isExecuting) {
      throw new Error(`Нельзя добавить операцию в выполняющийся батч ${batchId}`);
    }

    // Проверяем уникальность ID операции
    if (batch.operations.some(op => op.id === operation.id)) {
      throw new Error(`Операция с ID ${operation.id} уже существует в батче ${batchId}`);
    }

    batch.operations.push(operation);
    batch.progress.totalOperations = batch.operations.length;
    this.updateProgress(batch);
  }

  /**
   * Добавить несколько операций
   */
  addOperations(batchId: string, operations: BatchOperation[]): void {
    operations.forEach(operation => this.addOperation(batchId, operation));
  }

  /**
   * Выполнить батч
   */
  async executeBatch(batchId: string): Promise<BatchResult> {
    const batch = this.batches.get(batchId);
    if (!batch) {
      throw new Error(`Батч с ID ${batchId} не найден`);
    }

    if (batch.isExecuting) {
      throw new Error(`Батч ${batchId} уже выполняется`);
    }

    if (batch.operations.length === 0) {
      throw new Error(`Батч ${batchId} не содержит операций`);
    }

    batch.isExecuting = true;
    batch.startTime = new Date();
    batch.progress.startTime = batch.startTime;
    batch.abortController = new AbortController();

    this.statistics.totalBatches++;
    this.statistics.totalOperations += batch.operations.length;

    // Уведомляем о начале выполнения
    this.notifyBatchStart(batchId);

    try {
      // Валидация операций
      if (batch.config.validateBeforeExecution) {
        await this.validateOperations(batch);
      }

      // Сортировка операций по приоритету и зависимостям
      const sortedOperations = this.sortOperations(batch.operations);

      // Выполнение операций
      const operationResults = await this.executeOperations(batch, sortedOperations);

      // Создание результата
      batch.endTime = new Date();
      const totalExecutionTime = batch.endTime.getTime() - batch.startTime.getTime();
      
      const result: BatchResult = {
        batchId,
        success: operationResults.every(r => r.success),
        operationResults,
        totalExecutionTime,
        statistics: this.calculateBatchStatistics(operationResults),
        errors: operationResults.filter(r => !r.success).map(r => r.error!),
        metadata: {
          config: batch.config,
          startTime: batch.startTime,
          endTime: batch.endTime
        }
      };

      batch.result = result;
      batch.isExecuting = false;

      // Обновляем статистику
      if (result.success) {
        this.statistics.successfulBatches++;
      } else {
        this.statistics.failedBatches++;
      }

      this.statistics.activeBatches--;
      this.updateAverageExecutionTime(totalExecutionTime);

      // Уведомляем о завершении
      this.notifyBatchComplete(result);

      return result;

    } catch (error) {
      batch.isExecuting = false;
      batch.endTime = new Date();
      this.statistics.failedBatches++;
      this.statistics.activeBatches--;

      const batchError = error instanceof Error ? error : new Error(String(error));
      this.notifyBatchError(batchId, batchError);
      
      throw batchError;
    }
  }

  /**
   * Отменить выполнение батча
   */
  async cancelBatch(batchId: string): Promise<void> {
    const batch = this.batches.get(batchId);
    if (!batch) {
      throw new Error(`Батч с ID ${batchId} не найден`);
    }

    if (!batch.isExecuting) {
      return;
    }

    if (batch.abortController) {
      batch.abortController.abort();
    }

    batch.isExecuting = false;
    this.statistics.activeBatches--;
  }

  /**
   * Получить прогресс выполнения
   */
  getProgress(batchId: string): BatchProgress | null {
    const batch = this.batches.get(batchId);
    return batch ? { ...batch.progress } : null;
  }

  /**
   * Получить результат батча
   */
  getBatchResult(batchId: string): BatchResult | null {
    const batch = this.batches.get(batchId);
    return batch?.result ? { ...batch.result } : null;
  }

  /**
   * Удалить батч
   */
  removeBatch(batchId: string): boolean {
    const batch = this.batches.get(batchId);
    if (!batch) {
      return false;
    }

    if (batch.isExecuting) {
      throw new Error(`Нельзя удалить выполняющийся батч ${batchId}`);
    }

    this.batches.delete(batchId);
    return true;
  }

  /**
   * Получить все активные батчи
   */
  getActiveBatches(): string[] {
    return Array.from(this.batches.entries())
      .filter(([, batch]) => batch.isExecuting)
      .map(([id]) => id);
  }

  /**
   * Добавить слушателя событий
   */
  addEventListener(listener: BatchEventListener): void {
    this.eventListeners.push(listener);
  }

  /**
   * Удалить слушателя событий
   */
  removeEventListener(listener: BatchEventListener): void {
    const index = this.eventListeners.indexOf(listener);
    if (index > -1) {
      this.eventListeners.splice(index, 1);
    }
  }

  /**
   * Получить статистику
   */
  getStatistics(): BatchManagerStatistics {
    return {
      ...this.statistics,
      memoryUsage: this.calculateMemoryUsage()
    };
  }

  /**
   * Валидация операций
   */
  private async validateOperations(batch: BatchState): Promise<void> {
    const validationPromises = batch.operations
      .filter(op => op.validate)
      .map(async (op) => {
        const context = this.createExecutionContext(batch, op);
        const validation = await op.validate!(context);
        if (!validation.valid) {
          throw new Error(`Валидация операции ${op.id} не пройдена: ${validation.errors.join(', ')}`);
        }
        return validation;
      });

    await Promise.all(validationPromises);
  }

  /**
   * Сортировка операций по приоритету и зависимостям
   */
  private sortOperations(operations: BatchOperation[]): BatchOperation[] {
    // Топологическая сортировка с учетом зависимостей
    const sorted: BatchOperation[] = [];
    const visited = new Set<string>();
    const visiting = new Set<string>();
    const operationMap = new Map(operations.map(op => [op.id, op]));

    const visit = (operation: BatchOperation) => {
      if (visiting.has(operation.id)) {
        throw new Error(`Обнаружена циклическая зависимость в операции ${operation.id}`);
      }
      
      if (visited.has(operation.id)) {
        return;
      }

      visiting.add(operation.id);

      // Сначала обрабатываем зависимости
      if (operation.dependencies) {
        for (const depId of operation.dependencies) {
          const dependency = operationMap.get(depId);
          if (dependency) {
            visit(dependency);
          }
        }
      }

      visiting.delete(operation.id);
      visited.add(operation.id);
      sorted.push(operation);
    };

    // Сортируем по приоритету перед обработкой зависимостей
    const prioritySorted = [...operations].sort((a, b) => b.priority - a.priority);
    
    for (const operation of prioritySorted) {
      visit(operation);
    }

    return sorted;
  }

  /**
   * Выполнение операций
   */
  private async executeOperations(
    batch: BatchState,
    operations: BatchOperation[]
  ): Promise<BatchOperationResult[]> {
    switch (batch.config.executionStrategy) {
      case BatchExecutionStrategy.SEQUENTIAL:
        return this.executeSequential(batch, operations);
      
      case BatchExecutionStrategy.PARALLEL:
        return this.executeParallel(batch, operations);
      
      case BatchExecutionStrategy.MIXED:
        return this.executeMixed(batch, operations);
      
      default:
        throw new Error(`Неизвестная стратегия выполнения: ${batch.config.executionStrategy}`);
    }
  }

  /**
   * Последовательное выполнение
   */
  private async executeSequential(
    batch: BatchState,
    operations: BatchOperation[]
  ): Promise<BatchOperationResult[]> {
    const results: BatchOperationResult[] = [];

    for (const operation of operations) {
      if (batch.abortController?.signal.aborted) {
        break;
      }

      const result = await this.executeOperation(batch, operation);
      results.push(result);

      if (!result.success && batch.config.errorMode === BatchErrorMode.FAIL_FAST) {
        break;
      }

      this.updateOperationProgress(batch, result);
    }

    return results;
  }

  /**
   * Параллельное выполнение
   */
  private async executeParallel(
    batch: BatchState,
    operations: BatchOperation[]
  ): Promise<BatchOperationResult[]> {
    const semaphore = new Semaphore(batch.config.maxConcurrency);
    const promises = operations.map(operation => 
      semaphore.acquire().then(async (release) => {
        try {
          if (batch.abortController?.signal.aborted) {
            return this.createCancelledResult(operation);
          }

          const result = await this.executeOperation(batch, operation);
          this.updateOperationProgress(batch, result);
          return result;
        } finally {
          release();
        }
      })
    );

    return Promise.all(promises);
  }

  /**
   * Смешанное выполнение (группы по зависимостям)
   */
  private async executeMixed(
    batch: BatchState,
    operations: BatchOperation[]
  ): Promise<BatchOperationResult[]> {
    const results: BatchOperationResult[] = [];
    const completed = new Set<string>();
    const remaining = [...operations];

    while (remaining.length > 0 && !batch.abortController?.signal.aborted) {
      // Находим операции, которые можно выполнить (все зависимости выполнены)
      const ready = remaining.filter(op => 
        !op.dependencies || op.dependencies.every(dep => completed.has(dep))
      );

      if (ready.length === 0) {
        throw new Error('Обнаружена циклическая зависимость или недостающие зависимости');
      }

      // Выполняем готовые операции параллельно
      const batchResults = await this.executeParallel(batch, ready);
      results.push(...batchResults);

      // Отмечаем выполненные операции
      batchResults.forEach(result => {
        if (result.success) {
          completed.add(result.operationId);
        }
      });

      // Удаляем выполненные операции из списка
      ready.forEach(op => {
        const index = remaining.indexOf(op);
        if (index > -1) {
          remaining.splice(index, 1);
        }
      });

      // Проверяем режим обработки ошибок
      if (batch.config.errorMode === BatchErrorMode.FAIL_FAST) {
        const hasFailures = batchResults.some(r => !r.success);
        if (hasFailures) {
          break;
        }
      }
    }

    return results;
  }

  /**
   * Выполнение одной операции
   */
  private async executeOperation(
    batch: BatchState,
    operation: BatchOperation
  ): Promise<BatchOperationResult> {
    const startTime = Date.now();
    let attempts = 0;
    const maxRetries = operation.maxRetries || 1;

    this.notifyOperationStart(operation);

    while (attempts < maxRetries) {
      attempts++;
      
      try {
        const context = this.createExecutionContext(batch, operation);
        
        // Применяем таймаут если указан
        let executePromise = operation.execute(context);
        
        if (operation.timeout) {
          executePromise = this.withTimeout(executePromise, operation.timeout);
        }

        const result = await executePromise;
        const executionTime = Date.now() - startTime;

        const operationResult: BatchOperationResult = {
          operationId: operation.id,
          success: true,
          result,
          executionTime,
          attempts,
          metadata: operation.metadata
        };

        batch.operationResults.set(operation.id, operationResult);
        this.notifyOperationComplete(operationResult);
        
        return operationResult;

      } catch (error) {
        const executionTime = Date.now() - startTime;
        const operationError = error instanceof Error ? error : new Error(String(error));

        if (attempts >= maxRetries) {
          const operationResult: BatchOperationResult = {
            operationId: operation.id,
            success: false,
            error: operationError,
            executionTime,
            attempts,
            metadata: operation.metadata
          };

          batch.operationResults.set(operation.id, operationResult);
          this.notifyOperationError(operation.id, operationError);
          
          return operationResult;
        }

        // Ждем перед повторной попыткой
        await this.delay(Math.min(1000 * attempts, 5000));
      }
    }

    throw new Error('Неожиданное завершение выполнения операции');
  }

  /**
   * Создание контекста выполнения
   */
  private createExecutionContext(
    batch: BatchState,
    operation: BatchOperation
  ): BatchExecutionContext {
    return {
      batchId: batch.id,
      operation,
      previousResults: new Map(batch.operationResults),
      sharedData: batch.sharedData,
      abortSignal: batch.abortController?.signal,
      updateProgress: (progress) => this.updateCustomProgress(batch, progress)
    };
  }

  /**
   * Создание начального прогресса
   */
  private createInitialProgress(batchId: string): BatchProgress {
    const now = new Date();
    return {
      batchId,
      totalOperations: 0,
      completedOperations: 0,
      failedOperations: 0,
      skippedOperations: 0,
      percentage: 0,
      startTime: now,
      lastUpdate: now
    };
  }

  /**
   * Обновление прогресса
   */
  private updateProgress(batch: BatchState): void {
    batch.progress.lastUpdate = new Date();
    batch.progress.percentage = batch.progress.totalOperations > 0 
      ? Math.round((batch.progress.completedOperations / batch.progress.totalOperations) * 100)
      : 0;

    this.notifyProgressUpdate(batch.progress);
  }

  /**
   * Обновление прогресса после завершения операции
   */
  private updateOperationProgress(batch: BatchState, result: BatchOperationResult): void {
    if (result.success) {
      batch.progress.completedOperations++;
    } else {
      batch.progress.failedOperations++;
    }

    this.updateProgress(batch);
  }

  /**
   * Пользовательское обновление прогресса
   */
  private updateCustomProgress(batch: BatchState, progress: Partial<BatchProgress>): void {
    Object.assign(batch.progress, progress);
    this.updateProgress(batch);
  }

  /**
   * Расчет статистики батча
   */
  private calculateBatchStatistics(
    results: BatchOperationResult[]
  ): BatchStatistics {
    const executionTimes = results.map(r => r.executionTime);
    
    return {
      totalOperations: results.length,
      successfulOperations: results.filter(r => r.success).length,
      failedOperations: results.filter(r => !r.success).length,
      skippedOperations: 0,
      averageExecutionTime: executionTimes.reduce((a, b) => a + b, 0) / executionTimes.length || 0,
      minExecutionTime: Math.min(...executionTimes) || 0,
      maxExecutionTime: Math.max(...executionTimes) || 0,
      memoryUsage: this.calculateMemoryUsage()
    };
  }

  /**
   * Расчет использования памяти
   */
  private calculateMemoryUsage(): number {
    if (typeof process !== 'undefined' && process.memoryUsage) {
      return process.memoryUsage().heapUsed;
    }
    return 0;
  }

  /**
   * Обновление среднего времени выполнения
   */
  private updateAverageExecutionTime(executionTime: number): void {
    const totalTime = this.statistics.averageBatchExecutionTime * (this.statistics.totalBatches - 1) + executionTime;
    this.statistics.averageBatchExecutionTime = totalTime / this.statistics.totalBatches;
  }

  /**
   * Создание результата отмененной операции
   */
  private createCancelledResult(operation: BatchOperation): BatchOperationResult {
    return {
      operationId: operation.id,
      success: false,
      error: new Error('Операция отменена'),
      executionTime: 0,
      attempts: 0,
      metadata: operation.metadata
    };
  }

  /**
   * Добавление таймаута к промису
   */
  private withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    return Promise.race([
      promise,
      new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error(`Операция превысила таймаут ${timeoutMs}мс`)), timeoutMs)
      )
    ]);
  }

  /**
   * Задержка
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Методы уведомлений
  private notifyBatchStart(batchId: string): void {
    this.eventListeners.forEach(listener => listener.onBatchStart?.(batchId));
    this.emit('batchStart', batchId);
  }

  private notifyBatchComplete(result: BatchResult): void {
    this.eventListeners.forEach(listener => listener.onBatchComplete?.(result));
    this.emit('batchComplete', result);
  }

  private notifyBatchError(batchId: string, error: Error): void {
    this.eventListeners.forEach(listener => listener.onBatchError?.(batchId, error));
    this.emit('batchError', batchId, error);
  }

  private notifyOperationStart(operation: BatchOperation): void {
    this.eventListeners.forEach(listener => listener.onOperationStart?.(operation));
    this.emit('operationStart', operation);
  }

  private notifyOperationComplete(result: BatchOperationResult): void {
    this.eventListeners.forEach(listener => listener.onOperationComplete?.(result));
    this.emit('operationComplete', result);
  }

  private notifyOperationError(operationId: string, error: Error): void {
    this.eventListeners.forEach(listener => listener.onOperationError?.(operationId, error));
    this.emit('operationError', operationId, error);
  }

  private notifyProgressUpdate(progress: BatchProgress): void {
    this.eventListeners.forEach(listener => listener.onProgressUpdate?.(progress));
    this.emit('progressUpdate', progress);
  }
}

/**
 * Семафор для ограничения параллельности
 */
class Semaphore {
  private permits: number;
  private waitQueue: Array<() => void> = [];

  constructor(permits: number) {
    this.permits = permits;
  }

  async acquire(): Promise<() => void> {
    return new Promise((resolve) => {
      if (this.permits > 0) {
        this.permits--;
        resolve(() => this.release());
      } else {
        this.waitQueue.push(() => {
          this.permits--;
          resolve(() => this.release());
        });
      }
    });
  }

  private release(): void {
    this.permits++;
    if (this.waitQueue.length > 0) {
      const next = this.waitQueue.shift()!;
      next();
    }
  }
} 